import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { anlagenAPI } from '../services/api';
import { Plus, Search, QrCode, Settings } from 'lucide-react';
import Navbar from '../components/Navbar';

const Dashboard = () => {
  const [anlagen, setAnlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTyp, setFilterTyp] = useState('alle');
  const navigate = useNavigate();

  useEffect(() => {
    loadAnlagen();
  }, []);

  const loadAnlagen = async () => {
    try {
      const data = await anlagenAPI.getAll();
      setAnlagen(data);
    } catch (error) {
      console.error('Fehler beim Laden der Anlagen:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAnlagen = anlagen.filter(anlage => {
    const matchesSearch = 
      anlage.hersteller.toLowerCase().includes(searchTerm.toLowerCase()) ||
      anlage.modell.toLowerCase().includes(searchTerm.toLowerCase()) ||
      anlage.seriennummer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      anlage.standort_adresse.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterTyp === 'alle' || anlage.anlagentyp === filterTyp;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-title">Übersicht der Anlagen</h1>
          <div className="flex gap-3">
            <Link
              to="/scanner"
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition"
              data-testid="scan-qr-button"
            >
              <QrCode className="w-5 h-5" />
              QR-Code scannen
            </Link>
            <Link
              to="/anlage/neu"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
              data-testid="new-anlage-button"
            >
              <Plus className="w-5 h-5" />
              Neue Anlage
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Suchen nach Hersteller, Modell, Seriennummer oder Standort..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <select
              value={filterTyp}
              onChange={(e) => setFilterTyp(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="filter-select"
            >
              <option value="alle">Alle Typen</option>
              <option value="Kälteanlage">Kälteanlagen</option>
              <option value="Klimaanlage">Klimaanlagen</option>
              <option value="Klimakammer">Klimakammern</option>
              <option value="Lüftungsanlage">Lüftungsanlagen</option>
              <option value="Wärmepumpe">Wärmepumpen</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        ) : filteredAnlagen.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Keine Anlagen gefunden</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterTyp !== 'alle' 
                ? 'Versuchen Sie eine andere Suche oder ändern Sie den Filter'
                : 'Erstellen Sie Ihre erste Anlage, um zu beginnen'}
            </p>
            {!searchTerm && filterTyp === 'alle' && (
              <Link
                to="/anlage/neu"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
              >
                <Plus className="w-5 h-5" />
                Erste Anlage erstellen
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="anlagen-grid">
            {filteredAnlagen.map((anlage) => (
              <div
                key={anlage.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition p-6 cursor-pointer"
                onClick={() => navigate(`/anlage/${anlage.id}`)}
                data-testid={`anlage-card-${anlage.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full mb-2">
                      {anlage.anlagentyp}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900">{anlage.hersteller}</h3>
                    <p className="text-gray-600">{anlage.modell}</p>
                  </div>
                  <QrCode className="w-8 h-8 text-gray-400" />
                </div>
                
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Seriennummer:</span>
                    <span className="ml-2 text-gray-900 font-medium">{anlage.seriennummer}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Standort:</span>
                    <span className="ml-2 text-gray-900">{anlage.standort_gebaeude}, {anlage.standort_raum}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Leistung:</span>
                    <span className="ml-2 text-gray-900">{anlage.leistung}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Baujahr:</span>
                    <span className="ml-2 text-gray-900">{anlage.baujahr}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;